import { Box, Fade, Flex } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { RanaAlert, RanaHeading, RanaLoading } from "@nens/ranalib";

import { ProjectFilePreview } from "@/features/project-file-preview";
import { useCanSeeModelSchematisation } from "@/hooks/use-can-see-model-schematisation";
import { FileDataTypeEnum, RanaFileIcon } from "@/lib/rana-file-data-type";
import { ModelSchematisationDetails } from "@/lib/schematisation-details";
import { ProjectFile } from "@/types/api-model-simple-names";
import { descriptorIsLoading } from "@/utils/descriptor-is-loading";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { getSchematisationId } from "../utils/get-schematisation-id";

import { ProjectFileMetadata } from "./project-file-metadata";
import { SchematisationLiveSiteAction } from "./schematisation-live-site-action";

interface ProjectFileDetailsProps {
  file: ProjectFile;
  fileRef: string | undefined;
  selectedRevisionId: number | undefined;
}

export function ProjectFileDetails({ file, fileRef, selectedRevisionId }: ProjectFileDetailsProps) {
  const canSeeModelSchematisation = useCanSeeModelSchematisation();
  if (canSeeModelSchematisation && file.dataType === FileDataTypeEnum.Schematisation) {
    const schematisationId = getSchematisationId(file);
    if (!schematisationId) {
      if (descriptorIsLoading(file.descriptor)) {
        const progress = file.descriptor?.status.progress;
        return (
          <Flex mt={16} gap={4} flexDirection="column" w="full" alignItems="center">
            <Box>
              <RanaHeading type="h5">
                <FormattedMessage
                  id="projectFile.modelSchematisationProcessing"
                  defaultMessage="Model schematisation is being processed"
                />
              </RanaHeading>
            </Box>
            <Box flex="1" ml={4}>
              <RanaLoading progress={progress ? progress * 100 : undefined} isCentered />
            </Box>
          </Flex>
        );
      }
      return (
        <RanaAlert status="error" mt={16}>
          <FormattedMessage
            id="projectFile.model_schematisation_not_linked"
            defaultMessage="There is no model schematisation linked to this file"
          />
        </RanaAlert>
      );
    }

    return (
      <ModelSchematisationDetails
        title={getNameFromFileId(file.id)}
        headerActions={(hasReachedStickyPosition) => (
          <Fade in={hasReachedStickyPosition}>
            <SchematisationLiveSiteAction schematisationId={schematisationId} />
          </Fade>
        )}
        schematisationId={schematisationId}
        mode="project"
        selectedRevisionId={selectedRevisionId}
        preview={<ProjectFilePreview fileRef={fileRef} file={file} />}
      />
    );
  }

  return (
    <>
      <RanaHeading type="h5">
        <RanaFileIcon fileOrDirectory={file} boxSize={6} mx={1} /> {getNameFromFileId(file.id)}
      </RanaHeading>
      <ProjectFileMetadata file={file} fileRef={fileRef} />
      <ProjectFilePreview fileRef={fileRef} file={file} />
    </>
  );
}
